import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { Toast } from "@helpers/popups/Toast";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import {
  useApprovePrimaryOrderMutation,
  useUpdatePrimarySaleOrderMutation,
} from "@states/primary-sale-order/primary-sale-order.api";
import BackButton from "@components/buttons/BackButton";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { saleOrderSchema } from "@helpers/validation-schemas/primary-sale-order/sale-order.schema";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomDateField from "@components/form/CustomDateField";
import CustomSearchModalChooseField from "@components/form/CustomSearchModalChooseField";
import useGetAllCustomers from "@hooks/customer/useGetAllCustomers";
import CustomSelectField from "@components/form/CustomSelectField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { useLazyGetProductByBarcodeQuery } from "@states/product/product.api";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@states/store";
import { thousandSeperator } from "@utils/general/digit-separators";
import CustomInputField from "@components/form/CustomInputField";
import CustomButton from "@components/buttons/CustomButton";
import { productDetailColumns } from "@helpers/columns/primary-sale-order/product-detail.columns";
import useGetOnePrimarySaleorder from "@hooks/primary-sales-order/useGetOnePrimarySaleOrder";
import {
  addFormValues,
  addProductDetail,
  removeProductDetail,
  resetFormValues,
  updateProductDetail,
} from "@states/common/common.slice";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { TProduct } from "@type-defs/product/TProduct";
import { PRIMARY_SALE_ORDER } from "@utils/constants/primary-sale-order.constants";
import { calculatePercentage } from "@utils/general/cal-percent";
import useGetOneSecondarySalesorder from "@hooks/secondary-sales-order/useGetOneSecondarySaleOrder";
import { useLazyGetCustomerQuery } from "@states/customer/customer.api";
import { useApproveSecondaryOrderMutation } from "@states/secondary-sale-order/secondary-sale-order.api";

import useGetAllPaymentTypes from "@hooks/common/useGetAllPaymentTypes";
import AuthComponent from "@components/auth/component";

import PromotionCard from "@components/extra/sales-order/PromotionCard";
import DeleteButton from "@components/buttons/DeleteButton";
import useGetAllProducts from "@hooks/product/useGetAllProducts";

import {
  useGetAllPaymentTermsQuery,
  useGetWarehousesByUserQuery,
  useLazyGetProductDetailByWarehouseQuery,
  useGetAllSaleTypeQuery,
  useGetCustomerByUserQuery,
  useLazyGetAllPromotionNameListQuery,
  useLazyGetAllPromotionProductListQuery,
  useLazyGetAllWayWarehouseQuery,
} from "@states/common/common.api";
import SaleOrderPromotionDetailModal from "@components/modals/sales-order/SaleOrderPromotionDetailModal";
import PromotionIcon from "@assets/images/Icon.png";
import { useDraggable } from "react-use-draggable-scroll";
import { selectCurrentUser } from "@states/auth/auth.slice";
import { useLazyGetAllWarehouseProductListQuery } from "@states/common/common.api";

import { LOCAL_STORAGE_KEYS } from "@utils/constants/local-storeage-keys.constants";
import { roundedValue } from "@utils/general/celi-value";
type FormFields = z.infer<typeof saleOrderSchema>;

const OrderApprovalEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const productBarcodeRef = useRef(null);
  const [businessUnits, setBusinessUnits] = useState<any>([]);
  const user = useSelector(selectCurrentUser);
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_KEY);
  const userData = user || (storedUser ? JSON.parse(storedUser!) : null);
  const { data: userWarehouseData, error: fetchUserWarehousesError } =
    useGetWarehousesByUserQuery({
      user_id: userData?.user_id,
    });
  const { formValues } = useSelector((state: RootState) => state.common);

  const {
    secondarySalesOrderData,
    secondarySalesOrderRefetch,
    secondarySalesOrderLoading,
  } = useGetOneSecondarySalesorder(id!);
  const tableContainerRef = useRef<any>();
  const { events } = useDraggable(tableContainerRef);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [autoSelectedRangeByAmount, setAutoSelectedRangeByAmount] =
    useState("0");
  const [autoSelectedRangeByQty, setAutoSelectedRangeByQty]: any =
    useState("0");
  const [baseQty, setBaseQty]: any = useState(1);
  const [selectedPromotionItemList, setSelectedPromotionItemList] = useState(
    []
  );
  const [
    getProductDetailByWarehouse,
    {
      data: productDetailByWarehouseData,
      error: productDetailByWarehouseError,
      isLoading: productDetailByWarehouseLoading,
    },
  ] = useLazyGetProductDetailByWarehouseQuery();
  const [customerChannelId, setCustomerChannleId] = useState("");
  const [wayId, setWayId] = useState("");
  const defaultValues = {
    secondary_sales_order_id: secondarySalesOrderData?.secondary_sales_order_id,
    sales_date: secondarySalesOrderData?.sales_date,
    delivery_date: secondarySalesOrderData?.delivery_date,
    sale_promotion_id: secondarySalesOrderData?.sale_promotion_id,
    //@ts-ignore
    promotion_name: secondarySalesOrderData?.promotion_name,
    customer_id: secondarySalesOrderData?.customer_id,
    description: secondarySalesOrderData?.description,
    business_unit_id: secondarySalesOrderData?.business_unit_id,
    sale_type_id: secondarySalesOrderData?.sale_type_id,
    payment_type_id: secondarySalesOrderData?.payment_type_id,
    status: secondarySalesOrderData?.status,
    reason: secondarySalesOrderData?.reason,
    payment_terms_id: secondarySalesOrderData?.payment_terms_id,
    customer_name:
      secondarySalesOrderData?.customer_first_name +
      " " +
      secondarySalesOrderData?.customer_last_name,
    product_detail: secondarySalesOrderData?.product_detail,
    sub_total: secondarySalesOrderData?.sub_total,
    grand_total_amount: secondarySalesOrderData?.grand_total_amount,
    discount_amount: secondarySalesOrderData?.discount_amount,
    tax_amount: secondarySalesOrderData?.tax_amount,
    other_charges: secondarySalesOrderData?.other_charges,
    discount_type: secondarySalesOrderData?.discount_type,
    tax_type: secondarySalesOrderData?.tax_type || "AMT",
    credit_limit: secondarySalesOrderData?.credit_limit,
    credit_balance: secondarySalesOrderData?.credit_balance,
    current_amount: secondarySalesOrderData?.grand_total_amount,
    discount: secondarySalesOrderData?.discount,
    tax: secondarySalesOrderData?.tax,
  };

  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting }, //TODO: need to handle wrong field name errors, cause they are uncaught
    reset,
  } = useForm<FormFields>({
    defaultValues,
    resolver: zodResolver(saleOrderSchema),
  });
  const [
    getAllWayWarehouse,
    { data: wayWarehouseData, error: fetchWayWarehouseError },
  ] = useLazyGetAllWayWarehouseQuery();
  useEffect(() => {
    if (wayId) {
      getAllWayWarehouse({
        way_id: wayId,
      });
    }
  }, [wayId]);
  const { append, remove, update } = useFieldArray({
    control,
    name: "product_detail",
  });
  const [getAllWarehouseProductList, { data: warehouseProductData }] =
    useLazyGetAllWarehouseProductListQuery();
  useEffect(() => {
    //@ts-ignore
    if (wayWarehouseData?.data?.length > 0) {
      getAllWarehouseProductList({
        //@ts-ignore
        warehouse_id: wayWarehouseData?.data?.[0]?.warehouse_id,
      });
    }
  }, [wayWarehouseData]);
  const [
    approvePrimaryOrder,
    { isLoading, error: updateError, isSuccess, data },
  ] = useApproveSecondaryOrderMutation();
  const [getOneCustomer, { data: getOneCustomerData }] =
    useLazyGetCustomerQuery();
  const { customerData } = useGetAllCustomers({ limit: 1000 });
  const { data: paymentTermData } = useGetAllPaymentTermsQuery({ status: 1 });
  const { paymentTypeData } = useGetAllPaymentTypes();
  const { data: saleTypeData } = useGetAllSaleTypeQuery();
  const { productData: productGetAllData } = useGetAllProducts({
    limit: 10000,
  });

  const [
    getAllPromotionProductList,
    {
      data: promotionProductData,
      error: promotionProductDataError,
      isLoading: promotionProductDataLoading,
    },
  ]: any = useLazyGetAllPromotionProductListQuery();
  const [
    getAllPromotionNameList,
    {
      data: PromotionData,
      error: getAllPromotioDataError,
      isLoading: promotioDataLoading,
    },
  ]: any = useLazyGetAllPromotionNameListQuery();
  const [
    getProductByBarcode,
    { data: productData, isLoading: productLoading },
  ] = useLazyGetProductByBarcodeQuery();

  useErrorHandling(
    ...castTypeArr<TFailResponse>([updateError, productDetailByWarehouseError])
  );

  useEffect(() => {
    if (formValues?.customer_id) {
      getOneCustomer({
        id: formValues?.customer_id,
      });
    }
  }, [formValues?.customer_id]);

  useEffect(() => {
    if (getOneCustomerData?.data) {
      setCustomerChannleId(getOneCustomerData?.data?.customer_category_id);

      setBusinessUnits(getOneCustomerData?.data?.business_units || []);
    }
  }, [getOneCustomerData]);

  useEffect(() => {
    if (formValues?.business_unit_id) {
      let filterBusinessUnit = businessUnits.find((bu: any) => {
        return bu.business_unit_id == formValues?.business_unit_id;
      });
      if (filterBusinessUnit?.way_id) {
        setWayId(filterBusinessUnit.way_id);
      }
    }
  }, [formValues?.business_unit_id, businessUnits]);
  useEffect(() => {
    if (id) {
      secondarySalesOrderRefetch();
    }
  }, [id, secondarySalesOrderRefetch]);

  useEffect(() => {
    if (isSuccess && data) {
      Toast.fire({
        title: data.message,
        icon: "success",
      });

      navigate(PRIMARY_SALE_ORDER.BASE_PATH, {
        state: {
          reload: true,
        },
      });
    }
  }, [isSuccess, data, navigate, Toast]);

  useEffect(() => {
    const pd = defaultValues?.product_detail?.map((dtl: any) => ({
      product_id: dtl.product_id,
      warehouse_id: dtl.warehouse_id,
      is_promotion_item: dtl.is_promotion_item || false,
      is_promotion_item_list: dtl.is_promotion_item_list || [],
      unit_type_data: dtl.unit_type_data,
      warehouse_name: dtl.warehouse_name,
      available_qty: dtl.available_qty || 0,
      old_available_qty: dtl.available_qty || 0,
      //@ts-ignore
      // available_qty : productDetailByWarehouseData?.data?.available_qty,
      product_code: dtl.product_code,
      api_level: dtl.api_level || "-",
      product_name: dtl.product_name,
      unit_name: dtl.unit_name,
      unit_id: dtl.unit_id,
      sales_price: dtl.sales_price,
      qty: dtl.qty,
      amount: dtl.amount,
      discount_type: dtl.discount_type,
      discount_amount: dtl.discount_amount,
      tax_type: dtl.tax_type || "AMT",
      tax_amount: dtl.tax_amount,
      total_amount: dtl.total_amount,
      discount: dtl.discount,
      tax: dtl.tax,
    }));

    dispatch(addFormValues({ ...defaultValues, product_detail: pd }));

    reset({ ...defaultValues, product_detail: pd });
  }, [secondarySalesOrderData, reset, dispatch]);
  useEffect(() => {
    const pd = defaultValues?.product_detail?.map((dtl: any) => {
      if (dtl.product_id && dtl.warehouse_id) {
        getProductDetailByWarehouse({
          product_id: dtl.product_id,
          warehouse_id: dtl.warehouse_id,
        });
      }
      return {
        product_id: dtl.product_id,
        warehouse_id: dtl.warehouse_id,
        api_level: dtl.api_level || "-",
        is_promotion_item: dtl.is_promotion_item || false,
        is_promotion_item_list: dtl.is_promotion_item_list || [],
        unit_type_data: dtl.unit_type_data,
        warehouse_name: dtl.warehouse_name,
        available_qty: dtl.available_qty || 0,
        old_available_qty: dtl.available_qty || 0,
        //@ts-ignore
        // available_qty : productDetailByWarehouseData?.data?.available_qty,
        product_code: dtl.product_code,
        product_name: dtl.product_name,
        unit_name: dtl.unit_name,
        unit_id: dtl.unit_id,
        sales_price: dtl.sales_price,
        qty: dtl.qty,
        amount: dtl.amount,
        discount_type: dtl.discount_type,
        discount_amount: dtl.discount_amount,
        tax_type: dtl.tax_type || "AMT",
        tax_amount: dtl.tax_amount,
        total_amount: dtl.total_amount,
        discount: dtl.discount,
        tax: dtl.tax,
      };
    });
    console.log("pd==>", pd);
    dispatch(addFormValues({ ...defaultValues, product_detail: pd }));

    reset({ ...defaultValues, product_detail: pd });
  }, [secondarySalesOrderData, reset, dispatch]);
  useEffect(() => {
    const productResponse = productData as any as TSuccessResponse<TProduct>;

    if (productResponse && productResponse.data.length > 0) {
      const product = productResponse.data[0];

      const existingProductIds = formValues?.product_detail?.map(
        (pd: any) => pd.product_id
      );

      if (!existingProductIds?.includes(product.product_id)) {
        const productDetail = {
          product_id: product.product_id,
          product_code: product.product_code,
          product_name: product.product_name,
          unit_id: product.unit_id,
          unit_name: product.unit_name,
          api_level: product.api_level || "-",
          sales_price: product.secondary_sales_price,
          qty: 5,
          amount: 5 * product.secondary_sales_price,
          discount: 0,
          discount_amount: 0,
          discount_type: "AMT",
          tax: 0,
          tax_type: "AMT",
          tax_amount: 0,
          total_amount: 5 * product.secondary_sales_price,
        };

        append(productDetail);
        dispatch(addProductDetail(product));
      }

      //@ts-ignore
      productBarcodeRef.current.value = "";
    }
  }, [productData]);

  const handleRemove = (index: number) => {
    remove(index);
    dispatch(removeProductDetail(index));
  };
  useEffect(() => {
    if (formValues?.sale_promotion_id) {
      getAllPromotionProductList({
        sale_promotion_id: formValues?.sale_promotion_id,
      });
    }
  }, [formValues?.sale_promotion_id]);
  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const barcode = e.target.value;

    getProductByBarcode({ barcode });
  };
  useEffect(() => {
    if (wayId && customerChannelId && formValues?.sales_date) {
      console.log("start ");
      getAllPromotionNameList({
        customer_category_id: customerChannelId,
        way_id: wayId,
        start_date: formValues?.sales_date,
        type : 1
      });
    }
  }, [wayId, customerChannelId, formValues?.sales_date]);
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      // async stuff here
      // console.log(data, 'data');
      await asyncDispatcher(data);
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  useEffect(() => {
    // 🧮 Helper: calculate totals for a product
    function calcProductTotals(product: any) {
      const qty = Number(product.qty || 0);
      const base_qty = Number(product.base_qty || 1);
      let sale_price = Number(product.sales_price || 0);

      // 💡 Track discount for display
      let discount_amount = 0;

      // 🎁 Apply promotion discount if exists
      if (product.is_promotion_item && product._promo_discount) {
        const promoDiscount = Number(product._promo_discount || 0);
        const promoDiscountType = product._promo_discount_type || "AMT";

        if (promoDiscountType === "%") {
          // Percentage discount: reduce sale price by percentage
          const discountPerUnit = sale_price * (promoDiscount / 100);
          sale_price -= discountPerUnit;
          discount_amount = discountPerUnit * qty * base_qty;
        } else {
          // Fixed amount discount: reduce sale price
          sale_price -= promoDiscount;
          discount_amount = promoDiscount * qty * base_qty;
        }
      } else {
        // Normal discount
        const discountVal = Number(product.discount || 0);
        const discountType = product.discount_type || "AMT";
        const gross_amount = qty * base_qty * sale_price;

        if (discountType === "%") {
          discount_amount = gross_amount * (discountVal / 100);
        } else {
          discount_amount = Math.min(discountVal, gross_amount);
        }
      }

      const gross_amount = qty * base_qty * Number(product.sales_price || 0);

      // 🧾 Tax always applies on gross_amount
      const taxVal = Number(product.tax || 0);
      const taxType = product.tax_type || "%";
      const tax_amount =
        taxType === "%"
          ? gross_amount * (taxVal / 100)
          : Math.min(taxVal, gross_amount);

      const total_amount = Math.round(
        gross_amount - discount_amount + tax_amount
      );

      return {
        amount: gross_amount,
        discount_amount,
        tax_amount,
        total_amount: isNaN(total_amount) ? 0 : total_amount,
      };
    }

    // 🎁 Apply promotion logic
    function applyPromotionIfAny(product: any) {
      if (!product?.is_promotion_item) return calcProductTotals(product);

      const promo = product.is_promotion_item_list;
      if (!promo || !Array.isArray(promo.info))
        return calcProductTotals(product);

      const qty = Number(product.qty || 0);

      const appliedTier = promo.info.find(
        (info: any) =>
          Number(info.sale_range_from) <= qty &&
          qty <= Number(info.sale_range_to)
      );

      if (!appliedTier) {
        product.is_promotion_item = false;
        product._promo_discount = 0;
        product._promo_discount_type = 0;
        return calcProductTotals(product);
      }

      product._promo_discount = appliedTier.discount || 0;
      product._promo_discount_type = appliedTier.discount_type || 0;
      product._promo_bonus = appliedTier.bonus || 0;
      product._promo_cashback = appliedTier.cashback || 0;

      return calcProductTotals(product);
    }

    const subscription = watch((formValues, { name }) => {
      const { product_detail, ...theRest } = formValues;
      if (!Array.isArray(product_detail)) return;

      let totalBonus = 0;
      let totalCashback = 0;

      product_detail.forEach((prod, index) => {
        let updatedProduct = { ...prod };

        // 🟡 Handle product change → reset details
        if (name?.includes(`product_detail.${index}.product_id`)) {
          //@ts-ignore
          const foundProduct = warehouseProductData?.data?.find(
            (p: any) => p.product_id === updatedProduct.product_id
          );
          if (foundProduct) {
            updatedProduct = {
              ...updatedProduct,
              product_id: foundProduct.product_id,
              unit_id: foundProduct.unit_id,
              unit_type_data: foundProduct.unit_type_data,
              sales_price: foundProduct.primary_sales_price,
              tax: foundProduct.primary_sales_tax,
              tax_type: foundProduct.primary_sales_tax_type || "AMT",
              available_qty: foundProduct.available_qty || 0,
              old_available_qty: foundProduct.available_qty || 0,
              qty: 0,
              discount: 0,
              discount_type: "AMT",
              amount: 0,
              total_amount: 0,
              //@ts-ignore
              _promo_discount: 0,
              _promo_bonus: 0,
              _promo_cashback: 0,
              is_promotion_item: false,
              is_promotion_item_list: null,
            };
          }
        }

        // 🟡 Handle unit change → recalc available_qty
        if (
          name?.includes(`product_detail.${index}.unit_id`) ||
          name?.includes(`product_detail.${index}.product_id`)
        ) {
          const selectedUnit = updatedProduct.unit_type_data?.find(
            (u: any) => u.unit_id == updatedProduct.unit_id
          ) || { base_qty: 1 };
          const base_qty = Number(selectedUnit.base_qty) || 1;
          const oldQty = Number(updatedProduct.old_available_qty) || 0;

          updatedProduct.base_qty = base_qty;
          updatedProduct.available_qty =
            base_qty > 0 ? Math.floor(oldQty / base_qty) : oldQty;
        }

        // 🎁 Apply promotion if exists
        if (
          updatedProduct.product_id &&
          updatedProduct.unit_id &&
          promotionProductData
        ) {
          const promoDetail = promotionProductData.details.find(
            (d: any) =>
              d.product_id === updatedProduct.product_id &&
              d.unit_id === updatedProduct.unit_id
          );

          if (promoDetail) {
            updatedProduct.is_promotion_item = true;
            updatedProduct.is_promotion_item_list = { ...promoDetail };
          } else {
            updatedProduct.is_promotion_item = false;
            updatedProduct.is_promotion_item_list = null;
          }
        }

        // 🧾 Calculate totals
        const totals = applyPromotionIfAny(updatedProduct);
        updatedProduct = { ...updatedProduct, ...totals };

        // 🟢 Sum promotion bonuses & cashback
        //@ts-ignore
        totalBonus += Number(updatedProduct._promo_bonus || 0);
        //@ts-ignore
        totalCashback += Number(updatedProduct._promo_cashback || 0);

        // 🔄 Update each product
        //@ts-ignore
        update(index, updatedProduct);
        //@ts-ignore
        updateProductDetail({ index, product_detail: updatedProduct });
      });

      // 🧮 Calculate subtotal, tax, grand total
      const subTotal = product_detail.reduce(
        //@ts-ignore
        (acc, cur) => acc + (cur.total_amount || 0),
        0
      );

      const discountAmount =
        theRest.discount_type === "%"
          ? subTotal * (Math.min(theRest.discount || 0, 100) / 100)
          : Math.min(theRest.discount || 0, subTotal);

      const taxAmount =
        theRest.tax_type === "%"
          ? subTotal * ((theRest.tax || 0) / 100)
          : theRest.tax || 0;

      const otherCharges = theRest.other_charges || 0;

      dispatch(
        addFormValues({
          ...theRest,
          product_detail: product_detail.map((p) => ({ ...p })),
          sub_total: subTotal,
          discount_amount: discountAmount,
          discount: theRest.discount || 0,
          tax_amount: taxAmount,
          tax: theRest.tax || 0,
          other_charges: otherCharges,
          bonus: totalBonus,
          cash_back: totalCashback,
          grand_total_amount: Math.round(
            subTotal + otherCharges + taxAmount - discountAmount
          ),
        })
      );
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    dispatch,
    warehouseProductData,
    promotionProductData,
    getProductDetailByWarehouse,
  ]);

  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      // alert('hit')
      try {
        const { product_detail, discount, tax, ...rest } = formValues;

        const trimmedValues = {
          status: formValues.status,
          credit_balance: formValues.credit_balance,
          current_amount: formValues.current_amount,
          customer_id: formValues.customer_id,
          reason: formValues.reason,
        };

        await approvePrimaryOrder({
          id: id!,
          body: trimmedValues,
        });
      } catch (error) {
        console.log("Error", error);
      }
    },
    [approvePrimaryOrder, isSuccess, Toast, navigate, formValues]
  );

  // console.log(formValues, 'formValues');

  // console.log(secondarySalesOrderData, 'secondarySalesOrderData');
  console.error("errors", errors);
  return (
    <>
      <AuthComponent
        action="edit"
        module="Primary Sale Order"
        subModule="Order Approval"
        page
      >
        <SaleOrderPromotionDetailModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          data={selectedPromotionItemList}
          amount={autoSelectedRangeByAmount}
          qty={autoSelectedRangeByQty}
          base_qty={baseQty}
        />

        <PageWrapper>
          {(isLoading || productLoading || secondarySalesOrderLoading) && (
            <LoadingSpinner />
          )}
          <BackButton />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="ml-2 text-2xl font-[600] text-primary-dark uppercase">
                Edit Primary Sale Order Approval
              </h3>
            </div>
            <div className="grid grid-cols-6 gap-x-4 gap-y-8">
              <CustomDateField
                disabled
                errors={errors}
                name="sales_date"
                label="Sale Date"
                register={register}
                key="sales_date"
                //@ts-ignore
                control={control}
                value={secondarySalesOrderData?.sales_date}
              />

              <CustomDateField
                required
                disabled
                errors={errors}
                name="delivery_date"
                label="Delivery Date"
                register={register}
                key="delivery_date"
                //@ts-ignore
                control={control}
                value={secondarySalesOrderData?.delivery_date}
              />
              <CustomSelectField
                disabled
                required
                errors={errors}
                name="sale_type_id"
                label="Sale Type"
                placeHolder="Select Sale type"
                //@ts-ignore
                control={control}
                options={saleTypeData?.map((item: any) => ({
                  label: item.sale_type_name,
                  value: item.sale_type_id,
                }))}
                value={secondarySalesOrderData?.sale_type_id}
              />
              <CustomSearchModalChooseField
                disabled
                key={"customer_id"}
                errors={errors}
                colSpan={2}
                name={"customer_id"}
                label="Customer Name"
                title={"Customer List"}
                columns={[
                  {
                    name: "customer_name",
                    columnName: "Customer Name",
                  },
                  {
                    name: "phone_number",
                    columnName: "Phone Number",
                  },
                ]}
                register={register}
                // borderColor={field.borderColor}
                placeHolder="Choose customer name"
                setValue={setValue}
                data={customerData?.map((customer) => ({
                  customer_id: customer.customer_id,
                  customer_name:
                    customer.customer_first_name +
                    " " +
                    (customer.customer_last_name || "..."),
                  phone_number: customer.customer_phone1,
                }))}
                columnName={"Customer Name"}
                idName={"customer_id"}
                // value={field.value}
                nameName={"customer_name"}
                value={
                  secondarySalesOrderData?.customer_first_name +
                  " " +
                  (secondarySalesOrderData?.customer_last_name || "...")
                }
              />
              <CustomSelectField
                disabled
                errors={errors}
                name="business_unit_id"
                label="Business Unit"
                placeHolder="Select business unit"
                //@ts-ignore
                control={control}
                options={businessUnits?.map((bu: any) => ({
                  label: bu.business_unit_name,
                  value: bu.business_unit_id,
                }))}
              />
              <CustomSelectField
                disabled
                errors={errors}
                name="payment_type_id"
                label="Payment Type"
                placeHolder="Select payment type"
                //@ts-ignore
                control={control}
                options={paymentTypeData?.map((item: any) => ({
                  label: item.payment_type_name,
                  value: item.payment_type_id,
                }))}
                value={secondarySalesOrderData?.payment_type_id}
              />
              <CustomSelectField
                disabled
                errors={errors}
                name="payment_terms_id"
                label="Payment Term"
                placeHolder="Select payment term"
                //@ts-ignore
                control={control}
                options={paymentTermData?.map((item: any) => ({
                  label: item.payment_terms,
                  value: item.payment_terms_id,
                }))}
                value={secondarySalesOrderData?.payment_terms_id}
              />

              {/* <CustomTextAreaField
                        disabled
                        key={'remark'}
                        colSpan={3}
                        errors={errors}
                        name="remark"
                        register={register}
                    /> */}
              <CustomTextAreaField
                disabled
                key={"description"}
                errors={errors}
                colSpan={6}
                name="description"
                register={register}
              />
              <div className="flex justify-between col-span-6">
                <h3 className="ml-2 font-[600] text-primary-dark">
                  <span className=" text-[1.1rem]">Add Sale Order Detail</span>
                </h3>
              </div>
              <div className="flex flex-col justify-between col-span-6">
                <h3 className="ml-2 font-[500] uppercase text-primary-dark">
                  <span className=" text-[1.3rem]">Product Detail</span>
                </h3>
                {/* <div className={`flex flex-col p-2 mt-5 w-[25%]`}>
                            <label
                                className="mb-2 text-gray-700"
                                style={{ textTransform: 'capitalize' }}
                                htmlFor="name"
                            >
                                Product Barcode
                            </label>
                            <input
                                disabled
                                ref={productBarcodeRef}
                                onChange={handleBarcodeChange}
                                className={`px-2 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                            />
                        </div> */}
              </div>
              <div className="-mt-4 col-span-full">
                {/* <DataTable
                            className="pb-4"
                            responsive
                            striped
                            //@ts-ignore
                            columns={productDetailColumns(
                                errors,
                                register,
                                setValue,
                                handleRemove,
                                control,
                                true
                            )}
                            //@ts-ignore
                            data={formValues?.product_detail?.map((f, i) => ({
                                ...f,
                                index: i,
                            }))}
                        /> */}
                <div
                  ref={tableContainerRef}
                  {...events}
                  className="w-[100%] mx-auto cursor-grab active:cursor-grabbing overflow-x-auto hide-scrollbar"
                >
                  <table className="min-w-full overflow-x-scroll custom-table">
                    <thead className="h-12 py-3 text-xs font-light text-netural-600 bg-neutral-100">
                      <th className="text-xs font-light 2xl:text-sm ">No</th>

                      <th className="text-xs font-light 2xl:text-sm ">
                        Product Name
                      </th>

                      <th className="text-xs font-light 2xl:text-sm">Unit</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Sales Price
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Qty</th>

                      <th className="text-xs font-light 2xl:text-sm">Amount</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Discount
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Tax</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Total Amount
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Action</th>
                    </thead>
                    <tbody className="text-center">
                      {secondarySalesOrderData?.product_detail?.map(
                        (product_detail: any, i: number) => {
                          return (
                            <tr key={i}>
                              {product_detail?.is_promotion_item == true ? (
                                <td>
                                  <div className="w-[120px] flex justify-center items-center ">
                                    <img
                                      src={PromotionIcon}
                                      alt="promotion_icon"
                                      className=" object-cover cursor-pointer"
                                      onClick={() => {
                                        setSelectedPromotionItemList(
                                          product_detail?.is_promotion_item_list ||
                                            []
                                        );
                                        setAutoSelectedRangeByAmount(
                                          product_detail?.amount
                                        );
                                        setAutoSelectedRangeByQty(
                                          product_detail?.qty
                                        );
                                        setBaseQty(
                                          product_detail?.base_qty || 1
                                        );
                                        setIsModalOpen(true);
                                      }}
                                    />
                                  </div>
                                </td>
                              ) : (
                                <td>-</td>
                              )}
                              <td>
                                <CustomSelectField
                                  disabled
                                  options={
                                    //@ts-ignore
                                    warehouseProductData &&
                                    //@ts-ignore
                                    warehouseProductData?.data?.length > 0 &&
                                    //@ts-ignore
                                    warehouseProductData?.data?.map(
                                      //@ts-ignore
                                      (product) => {
                                        return {
                                          label: ` (
                                        ${product.product_name}-${product.product_code})`,
                                          value: product.product_id,
                                        };
                                      }
                                    )
                                  }
                                  classNames="w-[100%] min-w-[400px]"
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.product_id`}
                                  //@ts-ignore
                                  control={control}
                                  defaultValue={product_detail?.product_id}
                                />
                                {/* @ts-ignore */}
                                {errors?.product_detail?.[i].product_id && (
                                  <div className="mt-2 font-[300] italic text-[0.7rem] text-red-600">
                                    {
                                      errors?.product_detail?.[i]?.product_id
                                        ?.message
                                    }
                                  </div>
                                )}
                              </td>

                              <td>
                                <CustomSelectField
                                  disabled
                                  options={
                                    product_detail?.unit_type_data &&
                                    product_detail?.unit_type_data.map(
                                      (unit: any) => {
                                        return {
                                          label: unit.unit_name,
                                          value: unit.unit_id,
                                        };
                                      }
                                    )
                                  }
                                  classNames="w-[100%] min-w-[300px]"
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.unit_id`}
                                  //@ts-ignore
                                  control={control}
                                  value={product_detail?.unit_id}
                                  defaultValue={product_detail?.unit_id}
                                />
                              </td>
                              <td>
                                <CustomInputField
                                  readOnly
                                  disabled
                                  noLabel
                                  classNames="w-[100%]"
                                  errors={errors}
                                  name={`product_detail.${i}.sales_price`}
                                  register={register}
                                  setValue={setValue}
                                  value={product_detail.sales_price}
                                />
                              </td>

                              <td className="px-2">
                                <div
                                  className={`px-8 py-3 text-primary-dark border-[0.7px] w-[100px] focus:ring-1 
                                                               bg-zinc-100 cursor-not-allowed
                                                                    ring-primary focus:outline-none rounded border-primary-light
                                                            `}
                                >
                                  {product_detail.qty}
                                </div>
                                {/* <CustomInputField
                                                            disabled
                                                            inputType="number"
                                                            noLabel
                                                            classNames="w-[100%]"
                                                            errors={errors}
                                                            name={`product_detail.${i}.available_qty`}
                                                            register={register}
                                                        /> */}
                              </td>
                              <td>
                                {/* <CustomInputField
                                  disabled
                                  inputType="number"
                                  noLabel
                                  classNames="w-[100%]"
                                  errors={errors}
                                  name={`product_detail.${i}.amount`}
                                  register={register}
                                  setValue={setValue}
                                  value={product_detail.amount}
                                /> */}
                                <input
                                  disabled
                                  className="min-w-[100px] w-[100%] border border-gray-300 rounded px-2 py-3"
                                  value={product_detail?.amount}
                                />
                              </td>
                              <td>
                                <div className="flex items-center">
                                  <CustomInputField
                                    setValue={setValue}
                                    inputType="number"
                                    classNames="-mr-3 w-[130px]"
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.discount`}
                                    disabled
                                    register={register}
                                    defaultValue={product_detail.discount}
                                    value={product_detail.discount}
                                    min={0}
                                  />
                                  <select
                                    value={
                                      product_detail.discount_type || "AMT"
                                    }
                                    onChange={(e) =>
                                      setValue(
                                        `product_detail.${i}.discount_type`,
                                        e.target.value
                                      )
                                    }
                                    disabled
                                    className={`px-2 mx-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                  >
                                    <option value="AMT">AMT</option>
                                    <option value="%">%</option>
                                  </select>
                                </div>
                              </td>
                              <td>
                                <div className="flex items-center">
                                  <CustomInputField
                                    disabled
                                    setValue={setValue}
                                    inputType="number"
                                    classNames="-mr-3 w-[130px]"
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.tax`}
                                    register={register}
                                    defaultValue={product_detail.tax}
                                    value={product_detail.tax}
                                    min={0}
                                  />
                                  <select
                                    disabled
                                    value={product_detail.tax_type || "AMT"}
                                    onChange={(e) =>
                                      setValue(
                                        `product_detail.${i}.tax_type`,
                                        e.target.value
                                      )
                                    }
                                    className={`px-2 ml-2 mr-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                  >
                                    <option value="AMT">AMT</option>
                                    <option value="%">%</option>
                                  </select>
                                </div>
                              </td>
                              <td>
                                <input
                                  disabled
                                  className="min-w-[100px] w-[100%] border border-gray-300 rounded px-2 py-3"
                                  value={product_detail?.total_amount}
                                />

                                {/* <CustomInputField
                                  inputType="number"
                                  setValue={setValue}
                                  noLabel
                                  errors={errors}
                                  name={`product_detail.${i}.total_amount`}
                                  register={register}
                                  // value={product_detail?.total_amount}
                                  defaultValue={product_detail?.total_amount}
                                  disabled
                                /> */}
                              </td>
                              <td>
                                <div
                                  style={{
                                    width: "100px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <DeleteButton
                                    disabled={true}
                                    id={i}
                                    actionHandler={() => handleRemove(i)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="w-[48%] 2xl:w-[36%] mt-8 float-left">
                  {PromotionData && PromotionData.length > 0 && (
                    <div className="w-[600px] mt-6">
                      <div>
                        <div className="w-[200px] ">
                          {/* <CustomInputField
                            inputType="text"
                            setValue={setValue}
                            errors={errors}
                            label="Promotion Name"
                            name="promotion_name"
                            register={register}
                            value={defaultValues?.promotion_name}
                            disabled
                            required
                          /> */}
                          <CustomSelectField
                            // disabled
                            errors={errors}
                            disabled
                            label="Promotion Name"
                            name="sale_promotion_id"
                            placeHolder="Select Promotion Name"
                            //@ts-ignore
                            control={control}
                            options={PromotionData?.map((sp: any) => ({
                              label: sp.promotion_name,
                              value: sp.sale_promotion_id,
                            }))}
                            required
                            // onSelectChange={handlePromotionChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {promotionProductData &&
                    secondarySalesOrderData?.product_detail?.length > 0 && (
                      <PromotionCard
                        promotionProductData={promotionProductData}
                        bonus={formValues?.bonus}
                        cash_back={formValues?.cash_back}
                      />
                    )}
                </div>
                {secondarySalesOrderData?.product_detail?.length > 0 && (
                  <div className="bg-zinc-100 opacity-[0.8] rounded w-[48%] 2xl:w-[36%] mt-8 float-right p-6">
                    <div className="flex items-center mb-4">
                      <h5 className="text-[1.05rem] basis-3/12">Sub Total</h5>
                      <span className="s basis-1/12">:</span>
                      <div className="flex items-center basis-6/12"></div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(
                          secondarySalesOrderData.sub_total || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className=" basis-3/12 text-[1.05rem]">Discount</h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          disabled
                          classNames="-mr-3 w-[130px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"discount"}
                          placeHolder=" "
                          register={register}
                          defaultValue={
                            secondarySalesOrderData?.discount_type !== "AMT"
                              ? calculatePercentage(
                                  secondarySalesOrderData?.sub_total || 0,
                                  secondarySalesOrderData?.discount_amount || 0
                                )
                              : secondarySalesOrderData?.discount_amount
                          }
                        />
                        <select
                          disabled
                          value={secondarySalesOrderData?.discount_type}
                          {...register("discount_type")}
                          className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                        >
                          <option selected value="AMT">
                            AMT
                          </option>
                          <option value="%">%</option>
                        </select>
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        -
                        {thousandSeperator(
                          secondarySalesOrderData.discount_amount || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className=" basis-3/12 text-[1.05rem]">Tax</h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          disabled
                          // defaultValue={
                          //   secondarySalesOrderData.tax_type != "AMT"
                          //     ? calculatePercentage(
                          //         secondarySalesOrderData?.sub_total || 0,
                          //         secondarySalesOrderData?.tax_amount || 0
                          //       )
                          //     : secondarySalesOrderData?.tax_amount
                          // }
                          classNames="-mr-3 w-[130px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"tax"}
                          placeHolder=" "
                          register={register}
                        />
                        <select
                          disabled
                          value={secondarySalesOrderData?.tax_type}
                          {...register("tax_type")}
                          className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                        >
                          <option selected value="AMT">
                            AMT
                          </option>
                          <option value="%">%</option>
                        </select>
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(
                          secondarySalesOrderData.tax_amount || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-4">
                      <h5 className=" basis-3/12 text-[1.05rem]">
                        Other Charges
                      </h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          disabled
                          defaultValue={0}
                          classNames="-mr-3 w-[200px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"other_charges"}
                          placeHolder=" "
                          register={register}
                        />
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(
                          secondarySalesOrderData.other_charges || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className="text-xl font-semibold basis-3/12">
                        Grand Total
                      </h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12"></div>
                      <h5 className="text-xl font-semibold text-right basis-2/12 text-primary-dark">
                        {thousandSeperator(
                          secondarySalesOrderData.grand_total_amount || 0
                        )}
                      </h5>
                    </div>
                  </div>
                )}
              </div>
              <CustomInputField
                disabled
                errors={errors}
                name="credit_limit"
                label="Credit Limit"
                register={register}
                //@ts-ignore
                defaultValue={thousandSeperator(formValues?.credit_limit || 0)}
              />
              <CustomInputField
                disabled
                errors={errors}
                placeHolder={thousandSeperator(formValues?.credit_balance || 0)}
                name="credit_balance"
                label="Credit Balance"
                register={register}
                defaultValue={formValues?.credit_balance || 0}
              />
              <CustomInputField
                disabled
                errors={errors}
                name="current_amount"
                label="Current Amount"
                register={register}
                defaultValue={thousandSeperator(
                  formValues?.current_amount || 0
                )}
              />
              <CustomSelectField
                //@ts-ignore
                control={control}
                errors={errors}
                name="status"
                label="Status"
                options={[
                  {
                    label: "Approve & Open",
                    value: 3,
                  },
                  {
                    label: "Reject",
                    value: 4,
                  },
                ]}
                value={formValues.status || 3}
              />
              <CustomInputField
                register={register}
                name="reason"
                errors={errors}
                colSpan={4}
                value={formValues.reason || ""}
              />
            </div>
            <div className="ml-2 mt-14">
              <CustomButton
                isLarge
                title="Cancel"
                color="white"
                textColor="gray.800"
                className="mr-10"
                handleClick={() => navigate(-1)}
              />
              <CustomButton
                handleClick={handleSubmit(onSubmit)}
                isLarge
                title={isSubmitting ? "Loading..." : "Submit"}
                disabled={isSubmitting}
                type="submit"
              />
            </div>
            {errors.root && (
              <div className="mt-6 text-red-500">{errors.root.message}</div>
            )}
          </form>
        </PageWrapper>
      </AuthComponent>
    </>
  );
};

export default OrderApprovalEditPage;
